import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';

const InspectorInspectionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [findings, setFindings] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [reportFile, setReportFile] = useState(null);

  const fetchInspection = useCallback(async () => {
    try {
      const response = await api.get(`/inspections/inspector/${id}`);
      setInspection(response.data.data);
      setNotes(response.data.data.notes || '');
    } catch (error) {
      console.error('Error fetching inspection:', error);
      setError('Failed to load inspection details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInspection();
  }, [fetchInspection]);

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);
      setError('');

      await api.put(`/inspections/inspector/${id}/status`, {
        status: newStatus,
        notes: notes
      });

      // Refresh inspection data
      fetchInspection();
    } catch (error) {
      console.error('Error updating inspection status:', error);
      setError('Failed to update inspection status');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('report', file);

    try {
      setUploading(true);
      setError('');

      await api.post(`/inspections/inspector/${id}/report`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update status to completed and refresh
      await handleStatusUpdate('completed');
    } catch (error) {
      console.error('Error uploading report:', error);
      setError('Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const handleNotesUpdate = async () => {
    try {
      setUpdating(true);
      setError('');

      await api.put(`/inspections/inspector/${id}/notes`, {
        notes: notes
      });

      // Refresh inspection data
      fetchInspection();
    } catch (error) {
      console.error('Error updating notes:', error);
      setError('Failed to update notes');
    } finally {
      setUpdating(false);
    }
  };

  const handleStartInspection = async () => {
    try {
      setUpdating(true);
      setError('');

      await api.put(`/inspections/inspector/${id}/start`);

      // Refresh inspection data
      fetchInspection();
    } catch (error) {
      console.error('Error starting inspection:', error);
      setError('Failed to start inspection');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteInspection = async () => {
    if (!findings.trim() || !recommendations.trim()) {
      setError('Please provide both findings and recommendations before completing the inspection');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      const formData = new FormData();
      formData.append('findings', findings);
      formData.append('recommendations', recommendations);
      if (reportFile) {
        formData.append('report', reportFile);
      }

      await api.put(`/inspections/admin/${id}/complete`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Refresh inspection data
      fetchInspection();
    } catch (error) {
      console.error('Error completing inspection:', error);
      setError('Failed to complete inspection');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SCHEDULED':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'SCHEDULED':
        return 'Scheduled';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'COMPLETED':
        return 'Completed';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Inspection Not Found</h1>
          <p className="mt-2 text-gray-600">The inspection you're looking for doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/inspector/dashboard')}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          ← Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {inspection.inspectionMonth} Inspection Details
        </h1>
        <p className="mt-2 text-gray-600">Manage inspection details and upload reports</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(inspection.status)}`}>
          {getStatusText(inspection.status)}
        </span>
      </div>

      {/* Inspection Information */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Inspection Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Month</h3>
            <p className="mt-1 text-sm text-gray-900">{inspection.inspectionMonth}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Property Address</h3>
            <p className="mt-1 text-sm text-gray-900">{inspection.propertyAddress}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Property Type</h3>
            <p className="mt-1 text-sm text-gray-900">{inspection.propertyType}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Customer</h3>
            <p className="mt-1 text-sm text-gray-900">{inspection.customer?.name || 'N/A'}</p>
          </div>
          {inspection.scheduledDate && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Scheduled Date</h3>
              <p className="mt-1 text-sm text-gray-900">
                {format(new Date(inspection.scheduledDate), 'PPP')}
              </p>
            </div>
          )}
          {inspection.scheduledTime && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Scheduled Time</h3>
              <p className="mt-1 text-sm text-gray-900">{inspection.scheduledTime}</p>
            </div>
          )}
          {inspection.completedAt && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Completed At</h3>
              <p className="mt-1 text-sm text-gray-900">
                {format(new Date(inspection.completedAt), 'PPP p')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Inspection Notes</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              id="notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Add your inspection notes here..."
            />
          </div>
          <button
            onClick={handleNotesUpdate}
            disabled={updating}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {updating ? 'Updating...' : 'Update Notes'}
          </button>
        </div>
      </div>

      {/* Findings and Recommendations Section - Only show when inspection is in progress */}
      {inspection.status === 'IN_PROGRESS' && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Inspection Report</h2>
          <div className="space-y-6">
            <div>
              <label htmlFor="findings" className="block text-sm font-medium text-gray-700 mb-2">
                Findings <span className="text-red-500">*</span>
              </label>
              <textarea
                id="findings"
                rows={6}
                value={findings}
                onChange={(e) => setFindings(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Describe your inspection findings, observations, and any issues discovered..."
                required
              />
            </div>

            <div>
              <label htmlFor="recommendations" className="block text-sm font-medium text-gray-700 mb-2">
                Recommendations <span className="text-red-500">*</span>
              </label>
              <textarea
                id="recommendations"
                rows={6}
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Provide your recommendations for maintenance, repairs, or improvements..."
                required
              />
            </div>

            <div>
              <label htmlFor="reportFile" className="block text-sm font-medium text-gray-700 mb-2">
                Report File (Optional)
              </label>
              <input
                type="file"
                id="reportFile"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setReportFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload a detailed report file (PDF, DOC, DOCX, or images)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Report Upload Section */}
      {inspection.status === 'IN_PROGRESS' && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Upload Inspection Report</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="report" className="block text-sm font-medium text-gray-700 mb-2">
                Report File
              </label>
              <input
                type="file"
                id="report"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG
              </p>
            </div>
            {uploading && (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Uploading report...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report Display */}
      {inspection.reportFile && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Inspection Report</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Report has been uploaded and submitted.</p>
              <p className="text-xs text-gray-500 mt-1">
                File: {inspection.reportFile}
              </p>
            </div>
            <a
              href={`${process.env.REACT_APP_API_URL}/uploads/${inspection.reportFile}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
            >
              View Report
            </a>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Actions</h2>
        <div className="flex flex-wrap gap-3">
          {inspection.status === 'SCHEDULED' && (
            <button
              onClick={handleStartInspection}
              disabled={updating}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {updating ? 'Starting...' : 'Start Inspection'}
            </button>
          )}

          {inspection.status === 'IN_PROGRESS' && !inspection.reportFile && (
            <button
              onClick={handleCompleteInspection}
              disabled={updating || !findings.trim() || !recommendations.trim()}
              className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
            >
              {updating ? 'Completing...' : 'Complete Inspection'}
            </button>
          )}

          <button
            onClick={() => navigate('/inspector/dashboard')}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectorInspectionDetail;